import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Edit, Trash2, Save, X, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function AdminBroaderRegions() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', state: '', code: '' });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/admin/broader-regions`);
      setRegions(response.data);
    } catch (error) {
      console.error('Failed to fetch regions:', error);
      toast.error('Failed to load regions');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.state || !formData.code) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      if (editingId) {
        await axiosInstance.put(`${API_URL}/api/admin/broader-regions/${editingId}`, formData);
        toast.success('Region updated successfully');
      } else {
        await axiosInstance.post(`${API_URL}/api/admin/broader-regions`, formData);
        toast.success('Region created successfully');
      }
      fetchRegions();
      handleCancel();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save region');
    }
  };

  const handleEdit = (region) => {
    setFormData({ name: region.name, state: region.state, code: region.code });
    setEditingId(region._id || region.id);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this region? Products using it will need updating.')) return;
    try {
      await axiosInstance.delete(`${API_URL}/api/admin/broader-regions/${id}`);
      toast.success('Region deleted');
      fetchRegions();
    } catch (error) {
      toast.error('Failed to delete region');
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', state: '', code: '' });
    setEditingId(null);
    setIsEditing(false);
  };

  if (loading) return (
    <AdminLayout>
      <div className="flex justify-center items-center h-screen"><LoadingSpinner size="lg" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Broader Regions</h1>
            <p className="text-gray-600 mt-1">Manage regional zones for shipping (e.g., TN-South, KA-North)</p>
          </div>
          <Button onClick={() => setIsEditing(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" /> Add Region
          </Button>
        </div>

        {isEditing && (
          <Card className="border-2 border-green-200">
            <CardHeader className="bg-green-50">
              <CardTitle>{editingId ? 'Edit' : 'Add'} Region</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Region Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="TN-SOUTH"
                    maxLength={20}
                  />
                  <p className="text-xs text-gray-500 mt-1">e.g., TN-SOUTH, KA-NORTH</p>
                </div>
                <div>
                  <Label>State *</Label>
                  <select
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select State</option>
                    {indianStates.map(state => <option key={state} value={state}>{state}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Region Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="South Tamil Nadu"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={handleCancel}>
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                  <Save className="w-4 h-4 mr-1" /> Save
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {regions.map(region => (
            <Card key={region._id || region.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{region.code}</h3>
                      <p className="text-sm text-gray-600">{region.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{region.state}</p>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(region)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(region._id || region.id)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {regions.length === 0 && (
          <Card className="p-12 text-center">
            <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Regions Yet</h3>
            <p className="text-gray-600 mb-4">Create regions like TN-South, KA-North for shipping zones</p>
            <Button onClick={() => setIsEditing(true)} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" /> Create First Region
            </Button>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
